<template>
  <div class="dish-photo-editor">
    <div class="photo-heading">{{ label }} <span>Opcional</span></div>
    <img v-if="preview" :src="preview" class="photo-preview" :style="{ objectFit: contain ? 'contain' : 'cover' }" :alt="'Vista previa: ' + label" />
    <div class="photo-controls">
      <input ref="file" type="file" accept="image/jpeg,image/png,image/webp" class="photo-input" @change="chooseFile" />
      <el-button size="small" :loading="processing || uploading" @click="$refs.file.click()">
        {{ contain ? (preview ? 'Cambiar imagen' : 'Añadir imagen') : (preview ? 'Cambiar foto' : 'Añadir foto') }}
      </el-button>
      <el-button v-if="preview" size="small" :disabled="processing || uploading" @click="removePhoto">{{ contain ? 'Quitar imagen' : 'Quitar foto' }}</el-button>
      <el-button v-if="preview" size="small" :disabled="processing || uploading" icon="el-icon-crop" @click="openCrop">Recortar imagen</el-button>
    </div>
    <p>JPG, PNG o WebP de hasta 15 MB. Se ajustará y comprimirá al guardar los cambios.</p>
    <p v-if="!contain">Puedes recortar la imagen para elegir qué parte se publica.</p>
    <p v-else>La imagen se mostrará completa, respetando sus proporciones y su transparencia.</p>
    <p v-if="error" class="photo-error" role="alert">{{ error }}</p>
    <el-dialog title="Recortar imagen" custom-class="photo-crop-dialog" :visible.sync="cropping" append-to-body width="680px" :close-on-click-modal="false" @opened="drawCrop" @closed="cropSource = null">
      <p>Arrastra los bordes o las esquinas para recortar por arriba, abajo, izquierda o derecha. Arrastra el centro para mover el marco.</p>
      <canvas ref="cropCanvas" class="crop-canvas" @pointerdown="startDrag" @pointermove="drag" @pointerup="endDrag" @pointercancel="endDrag" aria-label="Vista previa del recorte" />
      <div class="crop-edge-controls"><label v-for="edge in cropEdges" :key="edge.key">{{ edge.label }} (%)<el-input-number v-model="cropBounds[edge.key]" :min="0" :max="edgeMax(edge.key)" :step="1" @change="drawCrop" /></label></div>
      <el-button size="small" @click="resetCrop">Restablecer recorte</el-button>
      <span slot="footer"><el-button @click="cropping=false">Cancelar</el-button><el-button type="primary" @click="applyCrop">Aplicar recorte</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import * as api from "../api/request";

export default {
  props: { value: { type: String, default: "" }, label: { type: String, default: "Foto del plato" }, contain: Boolean },
  data() { return { pending: null, localPreview: "", processing: false, uploading: false, error: "", cropping:false,cropSource:null,cropBounds:{left:0,right:0,top:0,bottom:0},cropEdges:[{key:'left',label:'Izquierda'},{key:'right',label:'Derecha'},{key:'top',label:'Arriba'},{key:'bottom',label:'Abajo'}],dragPoint:null }; },
  computed: { preview() { return this.localPreview || api.imageUrl(this.value); } },
  beforeDestroy() { this.releasePreview(); },
  methods: {
    async openCrop() {
      this.processing=true;this.error='';
      try {
        const image=await new Promise((resolve,reject)=>{const img=new Image();img.crossOrigin='anonymous';img.onload=()=>resolve(img);img.onerror=reject;img.src=this.preview;});
        this.cropSource=image;this.resetCrop();this.cropping=true;
        await this.$nextTick();this.drawCrop();
      } catch(e){this.error='No se pudo abrir la imagen para recortarla.';}finally{this.processing=false;}
    },
    cropRect() {
      const image=this.cropSource;if(!image)return null;
      const b=this.cropBounds;
      return {x:image.naturalWidth*b.left/100,y:image.naturalHeight*b.top/100,width:image.naturalWidth*(100-b.left-b.right)/100,height:image.naturalHeight*(100-b.top-b.bottom)/100};
    },
    drawCrop() {
      const canvas=this.$refs.cropCanvas,rect=this.cropRect();if(!canvas || !rect)return;
      const image=this.cropSource,scale=Math.min(600/image.naturalWidth,360/image.naturalHeight);
      canvas.width=Math.max(1,Math.round(image.naturalWidth*scale));canvas.height=Math.max(1,Math.round(image.naturalHeight*scale));
      const ctx=canvas.getContext('2d'),x=rect.x/image.naturalWidth*canvas.width,y=rect.y/image.naturalHeight*canvas.height,w=rect.width/image.naturalWidth*canvas.width,h=rect.height/image.naturalHeight*canvas.height;
      ctx.drawImage(image,0,0,canvas.width,canvas.height);ctx.fillStyle='rgba(0,0,0,.5)';ctx.fillRect(0,0,canvas.width,y);ctx.fillRect(0,y+h,canvas.width,canvas.height-y-h);ctx.fillRect(0,y,x,h);ctx.fillRect(x+w,y,canvas.width-x-w,h);
      ctx.strokeStyle='#526f60';ctx.lineWidth=3;ctx.strokeRect(x,y,w,h);ctx.fillStyle='#526f60';for(const [hx,hy] of [[x,y],[x+w/2,y],[x+w,y],[x,y+h/2],[x+w,y+h/2],[x,y+h],[x+w/2,y+h],[x+w,y+h]]){ctx.fillRect(hx-6,hy-6,12,12);ctx.strokeStyle='#fff';ctx.lineWidth=1;ctx.strokeRect(hx-6,hy-6,12,12);}
    },
    edgeMax(key){const opposite={left:'right',right:'left',top:'bottom',bottom:'top'};return 98-this.cropBounds[opposite[key]];},
    resetCrop(){this.cropBounds={left:0,right:0,top:0,bottom:0};this.drawCrop();},
    startDrag(event){const box=event.target.getBoundingClientRect(),b=this.cropBounds,x=(event.clientX-box.left)/box.width*100,y=(event.clientY-box.top)/box.height*100,edges=[];if(Math.abs(x-b.left)<14/box.width*100)edges.push('left');else if(Math.abs(x-(100-b.right))<14/box.width*100)edges.push('right');if(Math.abs(y-b.top)<14/box.height*100)edges.push('top');else if(Math.abs(y-(100-b.bottom))<14/box.height*100)edges.push('bottom');if(!edges.length && (x<b.left || x>100-b.right || y<b.top || y>100-b.bottom))return;this.dragPoint={x:event.clientX,y:event.clientY,bounds:{...b},edges};event.target.setPointerCapture(event.pointerId);},
    drag(event){if(!this.dragPoint)return;const box=event.target.getBoundingClientRect(),p=this.dragPoint,dx=(event.clientX-p.x)/box.width*100,dy=(event.clientY-p.y)/box.height*100,b={...p.bounds};if(p.edges.length){for(const edge of p.edges){const opposite={left:'right',right:'left',top:'bottom',bottom:'top'}[edge],delta=edge==='left'?dx:edge==='right'?-dx:edge==='top'?dy:-dy;b[edge]=Math.max(0,Math.min(98-b[opposite],p.bounds[edge]+delta));}}else{const sx=Math.max(-b.left,Math.min(b.right,dx)),sy=Math.max(-b.top,Math.min(b.bottom,dy));b.left+=sx;b.right-=sx;b.top+=sy;b.bottom-=sy;}this.cropBounds=b;this.drawCrop();},
    endDrag(){this.dragPoint=null;},
    async applyCrop() {
      const rect=this.cropRect();if(!rect)return;
      try {
        const canvas=document.createElement('canvas'),scale=Math.min(1,1280/Math.max(rect.width,rect.height));canvas.width=Math.max(1,Math.round(rect.width*scale));canvas.height=Math.max(1,Math.round(rect.height*scale));
        const context=canvas.getContext('2d');if(!this.contain){context.fillStyle='#fff';context.fillRect(0,0,canvas.width,canvas.height);}context.drawImage(this.cropSource,rect.x,rect.y,rect.width,rect.height,0,0,canvas.width,canvas.height);
        const blob=await new Promise(resolve=>canvas.toBlob(resolve,this.contain?'image/png':'image/jpeg',0.9));if(!blob)throw new Error();this.releasePreview();this.pending=blob;this.localPreview=URL.createObjectURL(blob);this.cropping=false;
      }catch(e){this.error='No se pudo aplicar el recorte. Vuelve a subir la imagen.';}
    },
    releasePreview() {
      if (this.localPreview) URL.revokeObjectURL(this.localPreview);
      this.localPreview = "";
    },
    async chooseFile(event) {
      const file = event.target.files[0];
      event.target.value = "";
      if (!file) return;
      this.error = "";
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 15 * 1024 * 1024) {
        this.error = "Elige una imagen JPG, PNG o WebP de hasta 15 MB.";
        return;
      }
      this.processing = true;
      const source = URL.createObjectURL(file);
      try {
        const image = await new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.src = source;
        });
        const scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext("2d");
        if (!this.contain) { context.fillStyle = "#fff"; context.fillRect(0, 0, canvas.width, canvas.height); }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise(resolve => canvas.toBlob(resolve, this.contain ? "image/png" : "image/jpeg", 0.85));
        if (!blob) throw new Error("Imagen no válida");
        this.releasePreview();
        this.pending = blob;
        this.localPreview = URL.createObjectURL(blob);
      } catch (error) {
        this.error = "No se pudo leer esta imagen. Prueba con otra foto JPG, PNG o WebP.";
      } finally {
        URL.revokeObjectURL(source);
        this.processing = false;
      }
    },
    removePhoto() {
      this.pending = null;
      this.releasePreview();
      this.error = "";
      this.$emit("input", "");
    },
    async upload() {
      if (this.processing) throw new Error("Espera a que termine de prepararse la foto.");
      if (!this.pending) return this.value;
      this.uploading = true;
      try {
        const result = await api.uploadDishImage(this.pending);
        this.pending = null;
        this.releasePreview();
        this.$emit("input", result.image);
        return result.image;
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.error || "No se pudo subir la foto. Inténtalo de nuevo.";
        throw error;
      } finally { this.uploading = false; }
    }
  }
};
</script>

<style scoped>
.dish-photo-editor { box-sizing:border-box; width: 100%; margin: 20px 0; padding: 16px; border: 1px solid #dce2e5; border-radius: 12px; text-align: left; }
.crop-canvas{display:block;max-width:100%;width:auto;height:auto;margin:16px auto;touch-action:none;cursor:move;background:repeating-conic-gradient(#eee 0% 25%,#fff 0% 50%) 50%/16px 16px}
.crop-edge-controls{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin-bottom:16px}.crop-edge-controls label{display:flex;flex-direction:column;gap:6px}.crop-edge-controls .el-input-number{width:100%}
.photo-heading { font-size: 16px; font-weight: 600; color: #37474f; margin-bottom: 12px; }
.photo-heading span { color: #687980; font-size: 12px; font-weight: 400; margin-left: 6px; }
.photo-preview { display: block; width: 100%; max-width: 320px; aspect-ratio: 4 / 3; object-fit: cover; border-radius: 10px; margin-bottom: 12px; }
.photo-controls { display: flex; flex-wrap: wrap; gap: 8px; }
.photo-input { display: none; }
.dish-photo-editor p { margin: 8px 0 0; color: #687980; font-size: 12px; line-height: 1.5; }
.dish-photo-editor .photo-error { color: #b23838; }
</style>
